import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { ArrowRight, CupSoda, Droplets, Beer, Zap, Filter } from "lucide-react";
import SeoHead from "@/components/SeoHead";

/**
 * Brands portfolio landing page.
 * Coca-Cola / PepsiCo styled — clean white surface, 4 flagship cards, animated
 * category filter (All / Soft Drinks / Water / Juices / Malt / Energy).
 */
const BRANDS = [
  {
    slug: "onecola",
    name: "OneCola",
    tagline: "The Original Cola Experience.",
    Icon: CupSoda,
    accent: "#DC2626",
    tint: "rgba(220,38,38,0.08)",
    categories: ["soft-drinks"],
    products: ["OneCola Original", "OneCola Zero Sugar", "OneCola Diet", "OneCola Lemon", "OneCola Orange", "OneCola Vanilla"],
    image: "https://customer-assets.emergentagent.com/job_brew-excellence-2/artifacts/1d3mzm63_ONE%20K%201.jpeg",
  },
  {
    slug: "onefresh",
    name: "OneFresh",
    tagline: "Naturally Refreshing Every Day.",
    Icon: Droplets,
    accent: "#10B981",
    tint: "rgba(16,185,129,0.08)",
    categories: ["water", "juices"],
    products: ["Spring Water", "Sparkling Water", "Fruit Juices", "Fruit Drinks", "Flavoured Water"],
    image: "https://customer-assets-jai6qajn.emergentagent.net/job_brew-excellence-2/artifacts/a1e204d901c476c3_OneFresh%20Juice%20Collection%20Display.png",
  },
  {
    slug: "maltora",
    name: "Maltora",
    tagline: "Premium Malt Refreshment.",
    Icon: Beer,
    accent: "#B45309",
    tint: "rgba(180,83,9,0.10)",
    categories: ["malt"],
    products: ["Maltora Classic", "Maltora Low Sugar", "Maltora Ginger Malt"],
    image: "https://customer-assets-jai6qajn.emergentagent.net/job_brew-excellence-2/artifacts/f623a65f071fedae_Maltora%20Premium%20Non-Alcoholic%20Malt%20Drink%20Showcase.png",
  },
  {
    slug: "voltx",
    name: "VoltX",
    tagline: "Power Your Momentum.",
    Icon: Zap,
    accent: "#2563EB",
    tint: "rgba(37,99,235,0.10)",
    categories: ["energy"],
    products: ["VoltX Original", "VoltX Sugar Free", "VoltX Tropical", "VoltX Extra Strength"],
    image: "https://customer-assets.emergentagent.com/job_a5d05ac1-3d02-4cad-955b-cc790acddfb1/artifacts/f7mr4dva_VOLTX%20R.jpeg",
  },
];

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "soft-drinks", label: "Soft Drinks" },
  { id: "water", label: "Water" },
  { id: "juices", label: "Juices" },
  { id: "malt", label: "Malt" },
  { id: "energy", label: "Energy Drinks" },
];

export default function Brands() {
  const [category, setCategory] = useState("all");

  const filtered = useMemo(() => {
    if (category === "all") return BRANDS;
    return BRANDS.filter(b => b.categories.includes(category));
  }, [category]);

  return (
    <div className="bg-white dark:bg-neutral-950 text-neutral-900 dark:text-white" data-testid="brands-page">
      <SeoHead
        title="Our Brands · Scitygate Brewing Company Limited"
        description="Explore SBC Ltd's portfolio of premium beverages — OneCola, OneFresh, Maltora and VoltX. Crafted in Manchester for consumers worldwide."
        canonical="/brands"
      />

      {/* Hero */}
      <section className="relative py-24 lg:py-32 border-b border-neutral-200 dark:border-white/10 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-40 dark:opacity-100">
          <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)" }} />
          <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(14,165,233,0.10) 0%, transparent 70%)" }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="text-[11px] tracking-[0.32em] uppercase font-semibold text-gold-500 mb-4">Our Portfolio</div>
          <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            Brands that <span className="italic text-gold-500">refresh</span> the world.
          </h1>
          <p className="mt-6 text-lg text-neutral-600 dark:text-neutral-300 max-w-2xl mx-auto leading-relaxed">
            From premium cola to natural spring water, malt refreshment to functional energy —
            every SBC Ltd brand is being developed to international quality standards for the United Kingdom and global markets.
          </p>
        </div>
      </section>

      {/* Filter bar */}
      <section className="sticky top-20 z-40 bg-white/85 dark:bg-neutral-950/85 backdrop-blur-md border-b border-neutral-200 dark:border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3 overflow-x-auto no-scrollbar">
          <Filter className="w-4 h-4 text-neutral-400 flex-none" />
          {CATEGORIES.map(c => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest font-semibold border transition-colors whitespace-nowrap ${
                category === c.id
                  ? "bg-neutral-900 dark:bg-white border-neutral-900 dark:border-white text-white dark:text-neutral-900"
                  : "border-neutral-200 dark:border-white/15 text-neutral-500 hover:border-neutral-400 hover:text-neutral-900 dark:hover:text-white"
              }`}
              data-testid={`brand-filter-${c.id}`}
            >
              {c.label}
            </button>
          ))}
          <div className="ml-auto text-xs text-neutral-400 hidden sm:block">
            <span className="font-semibold text-neutral-700 dark:text-neutral-200">{filtered.length}</span> of {BRANDS.length} brands
          </div>
        </div>
      </section>

      {/* Cards */}
      <section className="py-16 lg:py-20">
        <LayoutGroup>
          <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 gap-5 lg:gap-6">
            <AnimatePresence mode="popLayout">
              {filtered.map((brand, i) => (
                <BrandCard key={brand.slug} brand={brand} index={i} />
              ))}
            </AnimatePresence>
          </div>
        </LayoutGroup>
        {filtered.length === 0 && (
          <div className="text-center py-16 text-neutral-500">No brands in this category yet.</div>
        )}
      </section>

      {/* Corporate CTA */}
      <section className="py-20 border-t border-neutral-200 dark:border-white/10 bg-neutral-50 dark:bg-neutral-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Interested in distributing SBC brands?</h2>
          <p className="mt-4 text-neutral-600 dark:text-neutral-300">Join a growing network of authorised distributors across Africa, the Middle East and Europe.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/distributor" className="inline-flex items-center gap-2 bg-gold-400 hover:bg-gold-300 text-neutral-950 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-widest" data-testid="brands-page-cta-distributor">
              Become a Distributor <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/downloads" className="inline-flex items-center gap-2 border border-neutral-300 dark:border-white/20 hover:border-gold-400 text-neutral-900 dark:text-white rounded-full px-6 py-3 text-sm font-semibold">
              Download Catalogues
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function BrandCard({ brand, index }) {
  const { slug, name, tagline, Icon, accent, tint, products, image, categories } = brand;
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.45, delay: 0.05 * index }}
      className="group relative rounded-3xl overflow-hidden bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-white/10 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
      data-testid={`brand-card-${slug}`}
    >
      {/* image */}
      <div className="relative aspect-[16/9] overflow-hidden" style={{ background: tint }}>
        <img
          src={image}
          alt={name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold"
             style={{ background: "rgba(255,255,255,0.9)", color: accent }}>
          <Icon className="w-3.5 h-3.5" /> {categories[0].replace("-", " ")}
        </div>
      </div>

      {/* content */}
      <div className="p-6 lg:p-8">
        <div className="flex items-baseline gap-1">
          <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter leading-none" style={{ color: accent }}>{name}</h2>
          <sup className="text-sm font-bold" style={{ color: accent }}>®</sup>
        </div>
        <p className="mt-2 font-semibold text-neutral-800 dark:text-neutral-100">{tagline}</p>

        <ul className="mt-5 grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm text-neutral-600 dark:text-neutral-300">
          {products.map(p => (
            <li key={p} className="flex items-start gap-1.5">
              <span className="mt-1.5 w-1 h-1 rounded-full flex-none" style={{ background: accent }} />
              {p}
            </li>
          ))}
        </ul>

        <Link
          to={`/brands/${slug}`}
          className="mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest"
          style={{ color: accent }}
          data-testid={`brand-explore-${slug}`}
        >
          Explore {name.toUpperCase()} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.article>
  );
}
